import { z } from "zod";

import { cartIdZodSchema } from "../../../carts/cart-id";
import { isoAndUnixZodSchema } from "../../../date-time/iso-and-unix";
import type { DurationInMinutes } from "../../../date-time/time-spans";
import { deliveryZodSchema } from "../../../deliveries/delivery";
import { entityStateZodSchema } from "../../../entity-status";
import { contactInfoZodObject } from "../../../locations/contact-info";
import { orderGroupIdZodSchema } from "../../../order-groups/order-group-id";
import { priceZodSchema } from "../../../prices/price";
import { providerIdZodSchema } from "../../../providers/provider-id";
import { customerIdZodSchema } from "../../../users/customer-id";
import { userIdZodSchema } from "../../../users/user-id";

import { accommodationPurposeZodSchema } from "@@/shared/orders/order-accommodation.target-group";
import { productFormIdZodSchema } from "@@/shared/product-forms/product-form-id";
import { providerSlugZodSchema } from "@@/shared/providers/provider";
import { quoteIdZodSchema } from "@@/shared/quotes/quote-id";
import { translatableStringFactory } from "@@/shared/translations/translation";
import { orderEventIdZodSchema } from "../../order-events/events/_order-event-id";
import { orderIdZodSchema } from "../../order-id";
import { orderItemZodSchema } from "../../order-items/order-item";
import { orderStatusZodSchema } from "../../order-state";
import { orderTypeZodSchema } from "../../order-type";
import { miniOrderEventZodSchema } from "./order-event-mini";

const orderProjectionZodSchema = z
    .object({
        _id: orderIdZodSchema,
        _type: z.literal("ORDER_PROJECTION"),
        _createdAt: isoAndUnixZodSchema,
        _versionDate: isoAndUnixZodSchema,
        _status: entityStateZodSchema,
        _version: z.number().min(0),
        slug: z.string(),
        /**
         * If it doesn't exist, it's a `BUYABLE`
         * @type {OrderType}
         */
        type: orderTypeZodSchema.default("BUYABLE").optional(),
        providerId: providerIdZodSchema,
        providerSlug: providerSlugZodSchema.optional(),
        orderGroupId: orderGroupIdZodSchema,
        orderGroupReference: z.string(),
        orderItems: z.array(orderItemZodSchema),
        price: priceZodSchema,
        contactInfo: contactInfoZodObject,
        delivery: deliveryZodSchema,
        messageToProvider: z.string().nullable(),
        status: orderStatusZodSchema,
        tip: z.number(),
        tipPrice: priceZodSchema,
        totalPrice: priceZodSchema,
        customerId: userIdZodSchema.or(customerIdZodSchema),
        prepTime: z
            .number()
            .min(0) as unknown as z.ZodSchema<DurationInMinutes>, // todo
        leaveMyStuffOutside: z.boolean().optional(),
        extraOrderFee: priceZodSchema.optional(),
        destination: z.string().optional(),
        /**
         * The cart from which this order was created
         * @type {CartId}
         */
        cartId: cartIdZodSchema.optional(),
        /**
         * The cart hash from which this order was created
         * @type {string}
         */
        cartHash: z.string().optional(),
        createdEventId: orderEventIdZodSchema,
        events: z.array(miniOrderEventZodSchema),
        amountCapturable: z.number().int(),
        amountUnsettled: z.number().int(),
        amountSettled: z.number().int(),
        amountRefunded: z.number().int(),
        amountPendingRefund: z.number().int(),
        amountPaid: z.number().int(),
        amountUnpaid: z.number().int(),
        /**
         * Only set for new orders after orders went
         * events based in summer 2023
         * @type {boolean}
         */
        onPremiseOrder: z.boolean().optional(),
        orderGroupHasLegacyServiceFee: z.boolean(),
        // Status flags
        hasBeenCancelled: z.boolean(),
        hasBeenConfirmed: z.boolean(),
        hasBeenFulfilled: z.boolean(),
        hasBeenRejected: z.boolean(),
        hasPassed: z.boolean(),
        inactive: z.boolean(),
        hasPendingPayRequest: z.boolean(),
        hasPendingOnPremisePayment: z.boolean(),
        fulfillmentDate: isoAndUnixZodSchema,
        productFormIds: z.array(productFormIdZodSchema),
        channel: z
            .object({
                bookingId: z.string(),
                otaName: z.string(),
                paymentWithVirtualCard: z.boolean(),
                uniqueId: z.string(),
            })
            .optional(),

        fromQuote: quoteIdZodSchema.optional(),

        /** Quick fix, should have value only when type is accommodation, defaults to LEISURE */
        accommodationPurpose: accommodationPurposeZodSchema.optional(),
        /**
         * Quick fix
         * Contains count of accommodation type and sub types
         * to be used for e.g. for SCB statistics. \
         * Format: { id: count, ... }
         */
        accommodationSubTypeCount: z
            .record(
                // accommodationTypeAndSubTypeIdZodSchema, Commented out since zod returns a partial record: https://github.com/colinhacks/zod/issues/2623
                z.number(),
            )
            .optional(),
    })
    .transform((data, ctx) => {
        if (data.type !== "ACCOMMODATION" && !!data.accommodationPurpose) {
            // accommodationPurpose should never be set on a non-accommodation order
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: translatableStringFactory({
                    sv: "Boendemålgrupp kan bara vara sättas när ordern innehåller en boendebokning",
                    en: "Accommodation target group can only be set when the order contains an accommodation booking",
                }),
                path: ["accommodationPurpose"],
            });
            return z.NEVER;
        }
        if (data.type === "ACCOMMODATION" && !data.accommodationPurpose) {
            // Set default value if order is an accommodation order and no value is set
            return { ...data, accommodationPurpose: "LEISURE" };
        }

        // else.. we don't need to do anything
        return data;
    });

type OrderProjection = z.infer<typeof orderProjectionZodSchema>;

const isOrderProjection = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: any,
    options?: { exhaustiveCheck: boolean },
): item is OrderProjection => {
    return options?.exhaustiveCheck
        ? orderProjectionZodSchema.safeParse(item).success
        : item?._type === "ORDER_PROJECTION";
};

export { isOrderProjection, orderProjectionZodSchema };
export type { OrderProjection };
